import { Grid, Typography } from '@mui/material';
import React, { Dispatch, SetStateAction, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TenantWorkOrderSubmission } from 'vendor-marketplace-shared-library';

import AlertPopup from '../../../../components/common/AlertPopup';
import Button from '../../../../components/common/styled-mui-overrides/Button';
import AttachmentItem from '../../../../components/tenant-portal/AttachmentItem';
import { TenantPortalAttachment } from '../../../../types/tenant-portal';
import theme from '../../../../utilities/theme';

type TenantWorkOrderAttachmentSubmissionFormProps = {
  setTenantWorkOrderSubmission: SetStateAction<Dispatch<TenantWorkOrderSubmission>>;
  tenantWorkOrderSubmission: TenantWorkOrderSubmission | null;
  onSubmit: () => void;
  onUpload: (file: File) => void;
  onRemove: (attachment: TenantPortalAttachment) => void;
  attachments: TenantPortalAttachment[];
};

const TenantWorkOrderAttachmentSubmissionForm = (
  props: TenantWorkOrderAttachmentSubmissionFormProps,
) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [_, setSearchParams] = useSearchParams();

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      props.onUpload(event.target.files[0]);
    }
  };

  return (
    <Grid container sx={styles.mainContainer}>
      {/*No attachments*/}
      {props.attachments.length === 0 ? (
        <Grid item sx={styles.fullWidth}>
          <Grid container sx={styles.attachmentUploadContainer}>
            <Grid item>
              <Grid container sx={styles.headerContainer}>
                <Grid item>
                  <Typography variant='h6' fontWeight='bold'>
                    Upload a photo or video
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant='caption' color={theme.palette.secondary.main}>
                    Supported formats: PDF, JPEG
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            {/*Select File to Upload*/}
            <Grid item>
              <Button onClick={() => fileInputRef.current?.click()}>Upload file</Button>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        /*Attachments present*/
        <Grid item>
          <Grid container sx={styles.attachmentContainer}>
            {props.attachments.map((attachment: TenantPortalAttachment) => {
              return (
                <Grid item key={`${new Date()}-${attachment.file.name}`}>
                  <AttachmentItem
                    attachment={attachment}
                    onRemove={props.onRemove}
                    error={attachment.error}
                    loading={attachment.error}
                  />
                </Grid>
              );
            })}
            {/*Select File to Upload*/}
            <Grid item>
              <Button onClick={() => fileInputRef.current?.click()}>Upload file</Button>
            </Grid>
          </Grid>
        </Grid>
      )}

      {/*File upload input*/}
      <input
        type='file'
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelection}
      />

      {/*Page count*/}
      <Grid item>
        <Typography sx={styles.greyText} variant='caption'>
          2/3
        </Typography>
      </Grid>

      {/*Back and Next buttons*/}
      <Grid item sx={styles.fullWidth}>
        <Grid container sx={styles.buttonContainer}>
          <Grid item sx={styles.flexOne}>
            <Button
              sx={styles.button}
              onClick={() => {
                setSearchParams({ step: '1' });
              }}
            >
              Back
            </Button>
          </Grid>
          <Grid item sx={styles.flexOne} onClick={() => props.onSubmit()}>
            <Button>Next</Button>
          </Grid>
        </Grid>
      </Grid>
      <AlertPopup />
    </Grid>
  );
};

export default TenantWorkOrderAttachmentSubmissionForm;

const styles = {
  mainContainer: {
    flexDirection: 'column',
    rowGap: 4,
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  fullWidth: {
    width: '100%',
  },
  headerContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  attachmentUploadContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: theme.palette.secondary.light,
    backgroundColor: theme.palette.secondary.light,
    borderStyle: 'dashed',
    borderRadius: 4,
    rowGap: 4,
    padding: 4,
  },
  attachmentContainer: {
    flexDirection: 'column',
    rowGap: 2,
  },
  greyText: {
    color: theme.palette.secondary.main,
  },
  button: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.common.black,
    border: `1px ${theme.palette.secondary.dark} solid`,
    '&:hover': {
      backgroundColor: theme.palette.secondary.light,
    },
  },
  flexOne: {
    flex: 1,
  },
  buttonContainer: {
    columnGap: 1,
  },
};
